import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  LeadFilters,
  LeadNotificationConfig,
  PaginatedLeadsResponse,
} from 'src/app/models/lead.interface';
import { DigestNotificationConfig } from 'src/app/models/notification.interface';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private apiAddress: string = environment.apiAddress;

  constructor(private http: HttpClient) {}

  getConversationStats(id: string, init: string, term: string) {
    return this.http.get<any>(
      `${this.apiAddress}/bot/${id}/conversation_stats?init=${init}&term=${term}`
    );
  }

  getConversationDistributionStats(id: string, init: string, term: string) {
    return this.http.get<any>(
      `${this.apiAddress}/bot/${id}/conversation_time_distribution_stats?init=${init}&term=${term}`
    );
  }

  getConversationLeads(id: string, init: string, term: string) {
    return this.http.get<any>(
      `${this.apiAddress}/bot/${id}/conversation_leads?init=${init}&term=${term}`
    );
  }

  getConversationNonDocumentResponse(id: string, init: string, term: string) {
    return this.http.get<any>(
      `${this.apiAddress}/bot/${id}/conversation_non_document_based_responses?init=${init}&term=${term}`
    );
  }
  updateLeadsConfig(
    id: string,
    config: { leads_enabled: boolean; user_data_gathering_enabled: boolean }
  ) {
    return this.http.put(`${this.apiAddress}/bot/${id}/leads_config`, config);
  }

  getFeedbackStats(id: string, init: string, term: string) {
    return this.http.get<any>(
      `${this.apiAddress}/bot/${id}/feedback_stats?init=${init}&term=${term}`
    );
  }

  getMonthlyConversationsStats(id: string) {
    return this.http.get<any>(`${this.apiAddress}/bot/${id}/monthly_conversations_stats`);
  }

  getKeywords(id: string, init: string, term: string) {
    return this.http.get<{ keywords: { keyword: string; count: number }[] }>(
      `${this.apiAddress}/bot/${id}/keywords?init=${init}&term=${term}`
    );
  }

  // ─── Leads v2 ─────────────────────────────────────────────────

  getLeads(botId: string, filters: LeadFilters): Observable<PaginatedLeadsResponse> {
    let params = new HttpParams();
    if (filters.status) params = params.set('status', filters.status);
    if (filters.channel) params = params.set('channel', filters.channel);
    if (filters.search) params = params.set('search', filters.search);
    if (filters.date_from) params = params.set('date_from', filters.date_from);
    if (filters.date_to) params = params.set('date_to', filters.date_to);
    if (filters.page) params = params.set('page', filters.page.toString());
    if (filters.page_size) params = params.set('page_size', filters.page_size.toString());
    if (filters.sort_by) params = params.set('sort_by', filters.sort_by);
    if (filters.sort_order) params = params.set('sort_order', filters.sort_order);

    return this.http.get<PaginatedLeadsResponse>(`${this.apiAddress}/bot/${botId}/leads`, {
      params,
    });
  }

  updateLeadStatus(
    botId: string,
    leadId: string,
    status: string,
    convertedVia?: string
  ): Observable<any> {
    const body: any = { status };
    if (convertedVia) body.converted_via = convertedVia;
    return this.http.put(`${this.apiAddress}/bot/${botId}/leads/${leadId}/status`, body);
  }

  exportLeads(botId: string, filters: LeadFilters): Observable<Blob> {
    let params = new HttpParams();
    if (filters.status) params = params.set('status', filters.status);
    if (filters.channel) params = params.set('channel', filters.channel);
    if (filters.search) params = params.set('search', filters.search);
    if (filters.date_from) params = params.set('date_from', filters.date_from);
    if (filters.date_to) params = params.set('date_to', filters.date_to);

    return this.http.get(`${this.apiAddress}/bot/${botId}/leads/export`, {
      params,
      responseType: 'blob',
    });
  }

  getLeadNotificationConfig(workspaceId: string): Observable<any> {
    return this.http.get<any>(`${this.apiAddress}/workspace/${workspaceId}/lead-notifications`);
  }

  updateLeadNotificationConfig(
    workspaceId: string,
    config: LeadNotificationConfig
  ): Observable<any> {
    return this.http.put(`${this.apiAddress}/workspace/${workspaceId}/lead-notifications`, config);
  }

  getDigestNotificationConfig(workspaceId: string): Observable<any> {
    return this.http.get<any>(`${this.apiAddress}/workspace/${workspaceId}/digest-notifications`);
  }

  updateDigestNotificationConfig(
    workspaceId: string,
    config: DigestNotificationConfig
  ): Observable<any> {
    return this.http.put(
      `${this.apiAddress}/workspace/${workspaceId}/digest-notifications`,
      config
    );
  }
}
